import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiUsers, FiPlus, FiSave, FiArrowLeft } from 'react-icons/fi';
import { usersAPI, extensionsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './CreateEndUsers.css';

const CreateEndUsers = () => {
  const navigate = useNavigate();
  const { partner: currentPartner } = useAuth();
  
  const [extensions, setExtensions] = useState([]);
  const [selectedExtension, setSelectedExtension] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phoneNumber: '',
    role: 'user',
    plan: 5 // Default to Zeta plan (level 5)
  });

  // Fetch extensions available to current user
  useEffect(() => {
    const fetchExtensions = async () => {
      try {
        const response = await extensionsAPI.getAll();
        console.log('Extensions API response:', response);
        if (response.success) {
          const extensionsData = response.extensions || [];
          console.log('Extensions data:', extensionsData);
          extensionsData.forEach(ext => {
            console.log(`Extension ${ext.name}: plans =`, ext.plans);
          });
          setExtensions(extensionsData);
          // Don't auto-select the first extension - let user choose
        }
      } catch (error) {
        console.error('Error fetching extensions:', error);
        toast.error('Failed to fetch extensions');
      } finally {
        setLoading(false);
      }
    };
    fetchExtensions();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle extension selection
  const handleExtensionChange = (e) => {
    const selectedExt = extensions.find(ext => ext._id === e.target.value);
    console.log('Selected extension:', selectedExt);
    console.log('Extension plans:', selectedExt?.plans);
    console.log('Plans length:', selectedExt?.plans?.length);
    console.log('Condition check:', selectedExt && selectedExt.plans && selectedExt.plans.length > 0);
    setSelectedExtension(selectedExt);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setError(null);

    if (!selectedExtension) {
      setError('Please select an extension');
      setCreateLoading(false);
      return;
    }

    try {
      const userData = {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        password: formData.password.trim() || '123456',
        phoneNumber: formData.phoneNumber,
        extensionId: selectedExtension._id,
        role: formData.role,
        // Include plan for all extensions that have plans
        ...(selectedExtension.plans && { plan: formData.plan })
      };

      const response = await usersAPI.createEndUser(selectedExtension._id, userData);
      if (response.success) {
        toast.success('End user created successfully!');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          phoneNumber: '',
          role: 'user',
          plan: 5 // Default to Zeta plan (level 5)
        });
        // Navigate to dashboard after successful creation
        navigate('/dashboard');
      } else {
        setError(response.message || 'Failed to create end user');
      }
    } catch (err) {
      console.error('Error creating end user:', err);
      // Show the specific error message from the backend if available
      const errorMessage = err.message || 'Failed to create end user. Please try again.';
      setError(errorMessage);
    } finally {
      setCreateLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="create-end-users-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="create-end-users-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-left">
            <h2>
              <FiUsers />
              Create End User
            </h2>
            <p>Create a new end user and assign them to an extension</p>
          </div>
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            <FiArrowLeft />
            Back to Dashboard
          </button>
        </div>
      </div>

      <div className="page-content">
        <div className="form-container">
          <form onSubmit={handleSubmit} className="create-form">
            {/* Extension Selection */}
            <div className="form-group">
              <label htmlFor="extension">Extension *</label>
              <select
                id="extension"
                value={selectedExtension?._id || ''}
                onChange={handleExtensionChange}
                required
                className="form-control"
              >
                <option value="">Select an extension...</option>
                {extensions.map(extension => (
                  <option key={extension._id} value={extension._id}>
                    {extension.name}
                  </option>
                ))}
              </select>
            </div>

            {/* User Details */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="form-control"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password (Optional - defaults to 123456)</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Leave blank to use default password: 123456"
                className="form-control"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number (Optional)</label>
              <input
                type="text"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="Enter phone number (optional)"
                className="form-control"
              />
            </div>

            {/* Plan Selection - Show only for extensions that have plans with length > 0 */}
            {selectedExtension && selectedExtension.plans && selectedExtension.plans.length > 0 && (
              <div className="form-group">
                <label htmlFor="plan">Plan Level *</label>
                <select
                  id="plan"
                  name="plan"
                  value={formData.plan}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                >
                  <option value={0}>Alpha (Free) - FREE features</option>
                  <option value={1}>Beta (Level 1) - Beta features</option>
                  <option value={2}>Gamma (Level 2) - Gamma features + Advanced filtering</option>
                  <option value={3}>Delta (Level 3) - Delta features + Premium support</option>
                  <option value={4}>Epsilon (Level 4) - Epsilon features + Gender/Country filters</option>
                  <option value={5}>Zeta (Level 5) - Zeta features + All features included</option>
                </select>
                <small className="form-text text-muted">
                  Select the appropriate plan level for this user. Higher plans unlock more features.
                </small>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <div className="form-group">
              <div className="form_inner">
                <button
                  type="submit"
                  className="btn btn-blue btn-large"
                  disabled={createLoading || !selectedExtension}
                >
                  {createLoading ? (
                    <>
                      <div className="spinner"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <FiSave />
                      CREATE END USER
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateEndUsers; 